import { common } from '@kurtosys/ksys-app-template';
import {
	AuthenticationMode,
	IEndpointConfiguration,
	IEndpointsConfiguration,
	TRequestMethod,
} from '@kurtosys/types/api/common/index.js';
import { TEndpoints } from '@kurtosys/types/api/endpoints/index.js';
import { IRequestOptions } from '@kurtosys/types/api/IRequestOptions.js';

const DEFAULT_AUTH_MODE = AuthenticationMode.header;
export abstract class BaseKsysRequest<TRequestBody, TResponseBody> {
	abstract getExplicitEndpoint: () => string;
	abstract get endpointConfiguration(): IEndpointConfiguration;
	getExplicitMethod: () => TRequestMethod;
	// Events
	orchestrateResponse: (response: any) => TResponseBody;
	token: { value: string };
	options: IRequestOptions<TRequestBody>;
	endpointsConfiguration: IEndpointsConfiguration;
	constructor(
		token: { value: string } | undefined,
		options: IRequestOptions<TRequestBody>,
		endpointsConfiguration?: IEndpointsConfiguration,
	) {
		this.token = token;
		this.endpointsConfiguration = endpointsConfiguration;
		this.options = this.orchestrateOptions(options);
	}
	orchestrateOptions(overrideOptions: IRequestOptions<TRequestBody>): IRequestOptions<TRequestBody> {
		const rootOptions =
			(this.endpointsConfiguration && this.endpointsConfiguration.defaultEndpointConfiguration) || {};
		const endpointOptions = this.endpointConfiguration || {};
		return common.commonUtils.deepMergeObjectsWithOptions({}, rootOptions, endpointOptions, overrideOptions || {});
	}
	getEndpointConfiguration<K extends keyof TEndpoints>(
		pillar: K,
		endpoint: keyof TEndpoints[K],
	): IEndpointConfiguration {
		if (
			this.endpointsConfiguration &&
			this.endpointsConfiguration.endpoints &&
			this.endpointsConfiguration.endpoints[pillar]
		) {
			return this.endpointsConfiguration.endpoints[pillar][endpoint];
		}
		return {
			authenticationMode: DEFAULT_AUTH_MODE,
		};
	}
	getEndpoint(overrideOptions?: Partial<IRequestOptions<TRequestBody>>): string {
		const options: IRequestOptions<TRequestBody> = common.commonUtils.deepMergeObjects(
			this.options,
			overrideOptions,
		);
		const { endpoint, urlParams, queryString, placeholders } = options;
		let response = endpoint || this.getExplicitEndpoint();
		if (response && placeholders) {
			response = common.commonUtils.replacePlaceholders({ values: placeholders, text: response });
		}
		if (response && urlParams) {
			const join = response.endsWith('/') ? '' : '/';
			const cleanUrlParams = urlParams.map((urlParam) => encodeURIComponent(urlParam));
			response = `${response}${join}${cleanUrlParams.join('/')}`;
		}
		if (response && queryString) {
			const join = response.includes('?') ? '&' : '?';
			const queryStringPieces = Object.keys(queryString).reduce((acc, key) => {
				const value = queryString[key];
				acc.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
				return acc;
			}, []);
			response = `${response}${join}${queryStringPieces.join('&')}`;
		}
		return response;
	}
	abstract getUrl: (overrideOptions?: Partial<IRequestOptions<TRequestBody>>) => string;

	abstract execute: (overrideOptions?: Partial<IRequestOptions<TRequestBody>>) => Promise<TResponseBody>;

	// With worker
	// abstract callApi: (overrideOptions?: Partial<IRequestOptions<TRequestBody>>) => Promise<IHandleResponse>;
	// Without worker
	abstract callApi: (overrideOptions?: Partial<IRequestOptions<TRequestBody>>) => Promise<Response>;

	abstract handleResponse: (
		response: Response,
		overrideOptions?: Partial<IRequestOptions<TRequestBody>>,
	) => Promise<any>;
	// End
}
